import { useState, useRef, useEffect } from 'react';
import { Loader2, CheckCircle, RefreshCw, Sparkles, Type, Video, ArrowUp, Play, AlertTriangle } from 'lucide-react';
import { useStore } from '../store';
import { rewritePrompt, submitTask, subscribeTaskProgress } from '../api';

// ── Mode Select Card ──
function ModeSelectCard({ onSelect }: { onSelect: (mode: 'single' | 'batch') => void }) {
  return (
    <div className="bg-surface-2 border border-border rounded-xl overflow-hidden max-w-[85%] animate-fade-in-up">
      <div className="flex">
        <div className="w-1 bg-gradient-to-b from-brand to-accent flex-shrink-0" />
        <div className="p-4 flex-1">
          <p className="text-xs text-accent font-medium mb-3 flex items-center gap-1.5">
            <span>🎯</span> 选择任务模式
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => onSelect('single')}
              className="flex flex-col items-center gap-2 p-4 bg-surface-3 hover:bg-border rounded-xl transition-all hover:-translate-y-0.5"
            >
              <Sparkles size={24} className="text-brand" />
              <span className="text-sm font-medium">单个任务</span>
              <span className="text-xs text-text-muted">快速生成一个视频</span>
            </button>
            <button
              onClick={() => onSelect('batch')}
              className="flex flex-col items-center gap-2 p-4 bg-surface-3 hover:bg-border rounded-xl transition-all hover:-translate-y-0.5"
            >
              <Video size={24} className="text-accent" />
              <span className="text-sm font-medium">批量任务</span>
              <span className="text-xs text-text-muted">批量生成多个视频</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── AI Rewrite Confirm Card ──
function RewriteConfirmCard({
  task,
  onConfirm,
  onReject,
}: {
  task: { prompt: string; duration: number; aspectRatio: string; type: string };
  onConfirm: () => void;
  onReject: () => void;
}) {
  return (
    <div className="bg-surface-2 border border-border rounded-xl overflow-hidden max-w-[85%] animate-fade-in-up">
      <div className="flex">
        <div className="w-1 bg-gradient-to-b from-brand to-accent flex-shrink-0" />
        <div className="p-4 flex-1">
          <p className="text-xs text-accent font-medium mb-2 flex items-center gap-1.5">
            <Sparkles size={12} /> AI 改写结果
          </p>
          <div className="bg-surface-3 rounded-lg p-3 mb-3">
            <p className="text-sm text-text-primary leading-relaxed">{task.prompt}</p>
          </div>
          <div className="flex items-center gap-3 text-[11px] text-text-muted mb-4">
            <span className="flex items-center gap-1">
              <Type size={11} /> {task.type === 'video' ? '视频' : '图片'}
            </span>
            {task.duration && <span>⏱ {task.duration}s</span>}
            <span>📐 {task.aspectRatio}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onConfirm}
              className="flex items-center gap-1.5 px-4 py-2 bg-brand-gradient text-white text-sm font-medium rounded-lg hover:shadow-[var(--shadow-brand)] transition-all hover:-translate-y-0.5"
            >
              <CheckCircle size={14} /> 生成视频
            </button>
            <button
              onClick={onReject}
              className="flex items-center gap-1.5 px-4 py-2 bg-surface-3 text-text-secondary text-sm rounded-lg hover:bg-border transition-all"
            >
              <RefreshCw size={14} /> 重新描述
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Progress Card ──
function ProgressCard({ message, progress }: { message: string; progress?: number }) {
  return (
    <div className="bg-surface-2 border border-border rounded-xl max-w-[85%] p-4 animate-fade-in-up">
      <div className="flex items-center gap-3">
        <Loader2 size={16} className="text-brand animate-spin" />
        <div className="flex-1">
          <p className="text-sm text-text-primary">{message}</p>
          {progress !== undefined && (
            <div className="mt-2 h-1.5 bg-surface-3 rounded-full overflow-hidden">
              <div
                className="h-full bg-brand-gradient rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Result Card ──
function ResultCard({ url, prompt, onPreview }: { url: string; prompt: string; onPreview: (url: string) => void }) {
  return (
    <div className="bg-surface-2 border border-border rounded-xl overflow-hidden max-w-[85%] animate-fade-in-up">
      <div className="flex">
        <div className="w-1 bg-success flex-shrink-0" />
        <div className="p-4 flex-1">
          <p className="text-xs text-success font-medium mb-2 flex items-center gap-1.5">
            <CheckCircle size={12} /> 视频生成完成
          </p>
          <p className="text-xs text-text-muted mb-3 line-clamp-2">{prompt}</p>
          <button
            onClick={() => onPreview(url)}
            className="flex items-center gap-1.5 px-4 py-2 bg-brand-gradient text-white text-sm font-medium rounded-lg hover:shadow-[var(--shadow-brand)] transition-all hover:-translate-y-0.5"
          >
            <Play size={14} /> 预览视频
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Error Card ──
function ErrorCard({ message }: { message: string }) {
  return (
    <div className="bg-surface-2 border border-error/30 rounded-xl max-w-[85%] p-4 animate-fade-in-up">
      <div className="flex items-start gap-3">
        <AlertTriangle size={16} className="text-error mt-0.5" />
        <div>
          <p className="text-sm text-error font-medium">生成失败</p>
          <p className="text-xs text-text-muted mt-1">{message}</p>
        </div>
      </div>
    </div>
  );
}

// ── Welcome Card ──
function WelcomeCard({ onStart }: { onStart: () => void }) {
  return (
    <div className="max-w-[85%] animate-fade-in-up">
      <div className="bg-surface-2 border border-border rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center shadow-[var(--shadow-brand)]">
            <span className="text-xl">🐾</span>
          </div>
          <div>
            <h2 className="text-base font-semibold text-text-primary">VidClaw</h2>
            <p className="text-xs text-text-muted">说人话，出视频</p>
          </div>
        </div>
        <p className="text-sm text-text-secondary mb-4">
          你好！我是 VidClaw，一个 AI 视频生成助手。告诉我你想要什么视频，我会帮你生成。
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            '一段猫咪在樱花树下奔跑的视频',
            '赛博朋克风格的城市夜景',
            '海边日落的航拍镜头',
            '一杯咖啡的特写慢镜头',
          ].map((example) => (
            <button
              key={example}
              onClick={onStart}
              className="text-left text-xs text-text-muted bg-surface-3 rounded-lg p-3 hover:bg-border hover:text-text-secondary transition-all"
            >
              {example}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main ChatPanel ──
export function ChatPanel() {
  const { messages, addMessage, isSubmitting, setSubmitting, settings, setPreviewUrl, setGuidedStep, guidedStep } = useStore();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [pendingTask, setPendingTask] = useState<any>(null);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Load settings from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem('vidclaw_settings');
      if (saved) {
        const s = JSON.parse(saved);
        useStore.getState().setSettings(s);
      }
    } catch {}
  }, []);

  // Send message
  async function handleSend() {
    const text = input.trim();
    if (!text || isSubmitting) return;

    setInput('');
    setSubmitting(true);

    // Add user message
    addMessage({
      id: Date.now().toString(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    });

    // Step 1: AI rewrite
    try {
      addMessage({
        id: Date.now().toString() + '_rewrite_progress',
        role: 'system',
        content: '正在改写提示词...',
        timestamp: new Date(),
        type: 'progress',
      });

      const task = await rewritePrompt(text);

      // Remove progress message
      useStore.setState((s) => ({
        messages: s.messages.filter((m) => !m.id.endsWith('_rewrite_progress')),
      }));

      // Show rewrite result with confirm
      addMessage({
        id: Date.now().toString() + '_rewrite',
        role: 'assistant',
        content: '已为你优化提示词，请确认：',
        timestamp: new Date(),
        type: 'ai-rewrite',
        data: task,
      });

      setPendingTask(task);
      setGuidedStep('task-confirming');
    } catch (err: any) {
      useStore.setState((s) => ({
        messages: s.messages.filter((m) => !m.id.endsWith('_rewrite_progress')),
      }));
      addMessage({
        id: Date.now().toString() + '_error',
        role: 'system',
        content: `改写失败：${err.message}`,
        timestamp: new Date(),
        type: 'error',
      });
    } finally {
      setSubmitting(false);
    }
  }

  // Confirm and submit
  async function handleConfirm() {
    const task = pendingTask;
    if (!task) return;
    setPendingTask(null);
    setSubmitting(true);
    setGuidedStep('task-executing');

    addMessage({
      id: Date.now().toString() + '_submit',
      role: 'system',
      content: '正在提交生成任务...',
      timestamp: new Date(),
      type: 'progress',
    });

    try {
      const result = await submitTask({
        prompt: task.prompt,
        duration: task.duration,
        aspectRatio: task.aspectRatio,
        model_version: settings.model,
      });

      // Remove progress
      useStore.setState((s) => ({
        messages: s.messages.filter((m) => !m.id.endsWith('_submit')),
      }));

      addMessage({
        id: Date.now().toString() + '_submitted',
        role: 'system',
        content: `任务已提交（ID: ${result.taskId}），正在生成...`,
        timestamp: new Date(),
        type: 'progress',
      });

      // Poll with SSE
      subscribeTaskProgress(
        result.taskId,
        (data) => {
          if (data.status === 'completed') {
            useStore.setState((s) => ({
              messages: s.messages.filter((m) => !m.id.endsWith('_submitted')),
            }));
            addMessage({
              id: Date.now().toString() + '_done',
              role: 'assistant',
              content: '视频生成完成！',
              timestamp: new Date(),
              type: 'result',
              data: { url: data.videoUrl, prompt: task.prompt },
            });
            setGuidedStep('task-done');
            setSubmitting(false);
          } else if (data.status === 'failed') {
            useStore.setState((s) => ({
              messages: s.messages.filter((m) => !m.id.endsWith('_submitted')),
            }));
            addMessage({
              id: Date.now().toString() + '_fail',
              role: 'system',
              content: `生成失败：${data.error || '未知错误'}`,
              timestamp: new Date(),
              type: 'error',
            });
            setSubmitting(false);
          } else {
            // Update progress message
            useStore.setState((s) => ({
              messages: s.messages.map((m) =>
                m.id.endsWith('_submitted')
                  ? { ...m, content: data.message || '正在生成...' }
                  : m
              ),
            }));
          }
        },
        (error) => {
          addMessage({
            id: Date.now().toString() + '_sse_error',
            role: 'system',
            content: `进度连接中断：${error.message}`,
            timestamp: new Date(),
            type: 'error',
          });
          setSubmitting(false);
        }
      );
    } catch (err: any) {
      useStore.setState((s) => ({
        messages: s.messages.filter((m) => !m.id.endsWith('_submit')),
      }));
      addMessage({
        id: Date.now().toString() + '_submit_error',
        role: 'system',
        content: `提交失败：${err.message}`,
        timestamp: new Date(),
        type: 'error',
      });
      setSubmitting(false);
    }
  }

  function handleReject() {
    setPendingTask(null);
    setGuidedStep('welcome');
    addMessage({
      id: Date.now().toString() + '_reject',
      role: 'system',
      content: '已取消，请重新描述你的需求。',
      timestamp: new Date(),
    });
  }

  function handlePreview(url: string) {
    setPreviewUrl(url);
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  return (
    <div className="flex flex-col h-full bg-surface-0">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {/* Welcome */}
          {messages.length === 0 && guidedStep === 'welcome' && (
            <WelcomeCard onStart={() => {}} />
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {msg.role === 'user' ? (
                <div className="bg-brand/15 text-text-primary rounded-xl px-4 py-3 max-w-[80%]">
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                </div>
              ) : msg.type === 'ai-rewrite' && msg.data ? (
                <RewriteConfirmCard
                  task={msg.data}
                  onConfirm={handleConfirm}
                  onReject={handleReject}
                />
              ) : msg.type === 'result' && msg.data ? (
                <ResultCard
                  url={msg.data.url}
                  prompt={msg.data.prompt}
                  onPreview={handlePreview}
                />
              ) : msg.type === 'error' ? (
                <ErrorCard message={msg.content} />
              ) : msg.type === 'progress' ? (
                <ProgressCard message={msg.content} />
              ) : msg.type === 'mode-select' ? (
                <ModeSelectCard onSelect={(mode) => useStore.getState().setTaskMode(mode)} />
              ) : (
                <div className="bg-surface-2 border border-border rounded-xl px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-text-secondary leading-relaxed">{msg.content}</p>
                </div>
              )}
            </div>
          ))}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="border-t border-border-subtle px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <div className="relative bg-surface-1 border border-border rounded-xl overflow-hidden focus-within:border-brand/40 transition-all">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="描述你想生成的视频..."
              rows={1}
              className="w-full bg-transparent text-sm text-text-primary placeholder-text-muted px-4 py-3 pr-12 resize-none outline-none"
              style={{ minHeight: '44px', maxHeight: '120px' }}
              onInput={(e) => {
                const target = e.target as HTMLTextAreaElement;
                target.style.height = 'auto';
                target.style.height = Math.min(target.scrollHeight, 120) + 'px';
              }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isSubmitting}
              className="absolute right-2 bottom-2 p-2 rounded-lg bg-brand-gradient text-white disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-[var(--shadow-brand)] transition-all"
            >
              {isSubmitting ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={16} />}
            </button>
          </div>
          <p className="text-[10px] text-text-disabled text-center mt-2">
            Enter 发送 · Shift+Enter 换行 · Powered by DeepSeek + Seedance
          </p>
        </div>
      </div>
    </div>
  );
}
